// components/DocumentsPage.jsx
import { Form } from 'react-bootstrap';
import { FaFile, FaTrash, FaEye, FaUpload } from 'react-icons/fa';

const DocumentsPage = ({
  register,
  errors,
  customErrors,
  isFieldRequired,
  shouldShowField,
  commonDisabled,
  isUpdateMode,
  existingFiles,
  handleRemoveFile,
  AccountFormService,
  setCustomErrors,
  watch,
  hasError,
  getErrorMessage,
  isManagedAccount // AJOUT: Pour gérer les comptes administrés
}) => {
  const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf','image/bmp', 'image/webp', 'image/tiff', 'image/svg+xml', 'image/heic'];
  const DOCUMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

  const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
  const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;

  const renderExistingFile = (fieldName, label) => {
    const fileUrl = existingFiles[`${fieldName}Url`];
    const fileName = existingFiles[`${fieldName}FileName`] || 
                     existingFiles[`${fieldName}Url`]?.split('/').pop() ||
                     `Fichier ${label}`;
    
    if (!fileUrl) return null;

    return (
      <div className="mb-3 p-3 border rounded bg-light">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-bold text-muted">
            {label} existant:
          </label>
          <div className="btn-group">
            <a 
              href={fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-primary"
              data-bs-toggle="tooltip"
              title="Voir le fichier"
            >
              <FaEye size={12} />
            </a>
            <button 
              type="button" 
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleRemoveFile(fieldName)}
              data-bs-toggle="tooltip"
              title="Supprimer ce fichier"
            >
              <FaTrash size={12} />
            </button>
          </div>
        </div>
        <div className="d-flex align-items-center p-2 bg-white rounded border">
          <FaFile className="me-2 text-primary" />
          <span className="text-truncate" style={{ maxWidth: '300px' }}>
            {fileName}
          </span>
          <small className="text-muted ms-auto">
            <FaEye className="me-1" />
            Cliquez sur l'œil pour visualiser
          </small>
        </div>
        <div className="mt-2">
          <small className="text-info">
            <FaUpload className="me-1" />
            Pour remplacer ce fichier, téléchargez-en un nouveau ci-dessous
          </small>
        </div>
      </div>
    );
  };

  const renderFileInput = (fieldName, label, fileTypes, maxSize, isRequired = false, showForManagedAccount = true) => {
    // CORRECTION: Pour les comptes administrés, certains documents ne doivent pas être affichés
    if (isManagedAccount) {
      const forbiddenForManagedAccounts = [
        'cni', 
        'proofOfAddress', 
        'numeroIdentificationFiscale'
      ];
      
      if (forbiddenForManagedAccounts.includes(fieldName)) {
        return null; // Ne pas afficher ces champs pour les comptes administrés
      }
    }

    const hasExistingFile = existingFiles[`${fieldName}Url`];
    const isActuallyRequired = isRequired && !hasExistingFile; // Pas obligatoire si fichier existe déjà

    return (
      <Form.Group className="mb-4">
        <Form.Label className="fw-bold">
          {label} {isActuallyRequired && <span className="text-danger">*</span>}
          {hasExistingFile && (
            <span className="badge bg-success bg-opacity-10 border-opacity-25 border-success text-success rounded-2 ms-2">
              <FaFile className="me-1" size={10} />
              Fichier présent
            </span>
          )}
        </Form.Label>
        
        {/* Afficher le fichier existant en mode édition */}
        {isUpdateMode && hasExistingFile && renderExistingFile(fieldName, label)}
        
        {/* Champ de téléchargement */}
        <div className="input-group">
          <Form.Control
            type="file"
            accept={fileTypes.join(',')}
            {...register(fieldName, {
              required: isActuallyRequired ? "Ce fichier est requis" : false
            })}
            className={`form-control ${
              errors[fieldName] || hasError(fieldName) ? 'is-invalid' : ''
            } ${
              (watch(fieldName)?.length > 0 || hasExistingFile) && 
              !hasError(fieldName) ? 'is-valid' : ''
            }`}
            disabled={commonDisabled || (isManagedAccount && fieldName === 'email')}
            onChange={(e) => {
              AccountFormService.validateFile(
                e.target.files, 
                fieldName, 
                fileTypes, 
                maxSize, 
                setCustomErrors
              );
            }}
          />
          <button 
            className="btn btn-primary" 
            style={{ marginLeft:'-7px' }}
            type="button"
            onClick={() => document.getElementById(`${fieldName}-help`).classList.toggle('d-none')}
          >
            <FaEye />
          </button>
        </div>

        {/* Instructions */}
        <div id={`${fieldName}-help`} className="form-text d-none">
          <small>
            <strong>Formats acceptés :</strong> {fileTypes.map(t => t.split('/').pop()).join(', ')}
            <br/>
            <strong>Taille max :</strong> {(maxSize / (1024 * 1024)).toFixed(0)} MB
          </small>
        </div>

        {/* Messages d'erreur */}
        {(errors[fieldName] || hasError(fieldName)) && (
          <div className="invalid-feedback d-block">
            {errors[fieldName]?.message || getErrorMessage(fieldName)}
          </div>
        )}
        
        {customErrors[fieldName] && (
          <div className="text-danger small mt-1">{customErrors[fieldName]}</div>
        )}
        
        {/* Message de succès */}
        {watch(fieldName)?.length > 0 && !errors[fieldName] && !customErrors[fieldName] && (
          <div className="valid-feedback d-block">
            <FaFile className="me-1" />
            Nouveau fichier sélectionné pour remplacement
          </div>
        )}
        
        {/* Message pour fichiers existants */}
        {!watch(fieldName)?.length > 0 && hasExistingFile && (
          <div className="alert alert-info mt-2 p-2 small">
            <FaFile className="me-1" />
            <strong>Fichier existant :</strong> {existingFiles[`${fieldName}FileName`] || 'Fichier téléchargé'}
            <br/>
            <small className="text-muted">
              Cliquez sur les boutons ci-dessus pour visualiser ou supprimer ce fichier.
            </small>
          </div>
        )}
      </Form.Group>
    );
  };

  return (
    <div className="page-content">
      <div className="page-header mb-4">
        <h2 className="h4 mb-2">Documents</h2>
        <p className="text-muted">
          {isUpdateMode ? (
            <>
              <FaEye className="me-2" />
              Visualisez, supprimez ou remplacez les documents existants.
              <br/>
              <small className="text-info">
                Les fichiers marqués d'un <span className="badge bg-success bg-opacity-10 border-opacity-25 border-success text-success rounded-2">Fichier présent</span> sont déjà enregistrés.
              </small>
            </>
          ) : (
            "Téléchargez les documents requis selon le type de compte sélectionné."
          )}
        </p>
      </div>

      {/* Documents Obligatoires */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h5 className="mb-0 text-black">
            Documents {isUpdateMode ? 'du Compte' : 'Obligatoires'}
            {isManagedAccount && (
              <span className="badge bg-warning text-dark ms-2">
                <FaFile className="me-1" />
                Compte Administré - Documents restreints
              </span>
            )}
          </h5>
        </div>
        <div className="card-body">
          {shouldShowField('picture') && (
            renderFileInput(
              'picture', 
              'Photo d\'identité 1', 
              IMAGE_TYPES, 
              MAX_IMAGE_SIZE, 
              isFieldRequired('picture')
            )
          )}
          
          {shouldShowField('photoIdentite') && (
            renderFileInput(
              'photoIdentite', 
              'Photo d\'identité 2', 
              IMAGE_TYPES, 
              MAX_IMAGE_SIZE, 
              isFieldRequired('photoIdentite')
            )
          )}
          
          {shouldShowField('cni') && !isManagedAccount && ( // Masquer pour comptes administrés
            renderFileInput(
              'cni', 
              'CNI', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('cni')
            )
          )}
          
          {shouldShowField('proofOfAddress') && !isManagedAccount && ( // Masquer pour comptes administrés
            renderFileInput(
              'proofOfAddress', 
              'Justificatif de domicile', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('proofOfAddress')
            )
          )}
          
          {shouldShowField('proofOfRevenue') && (
            renderFileInput(
              'proofOfRevenue', 
              'Justificatif de revenu', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('proofOfRevenue')
            )
          )}

          {/* Documents pour Entreprises */}
          {shouldShowField('journalAnnoncesLegalesFile') && (
            renderFileInput(
              'journalAnnoncesLegalesFile', 
              'Procès-verbal du conseil d\'administration', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('journalAnnoncesLegalesFile')
            )
          )}

          {shouldShowField('banqueDomiciliationFile') && (
            renderFileInput(
              'banqueDomiciliationFile', 
              'Procès-verbal de l\'assemblée générale constitutive', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('banqueDomiciliationFile')
            )
          )}

          {shouldShowField('referenceBancaireEntrepriseFile') && (
            renderFileInput(
              'referenceBancaireEntrepriseFile', 
              'Référence Bancaire de l\'Entreprise', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('referenceBancaireEntrepriseFile')
            )
          )}

          {shouldShowField('statut') && (
            renderFileInput(
              'statut', 
              'Statut', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('statut')
            )
          )}

          {shouldShowField('attestationImmatriculation') && (
            renderFileInput(
              'attestationImmatriculation', 
              'Attestation d\'immatriculation', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('attestationImmatriculation')
            )
          )}

          {shouldShowField('annonceLegale') && (
            renderFileInput(
              'annonceLegale', 
              'Annonce Légale', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('annonceLegale')
            )
          )}

          {shouldShowField('procuration') && (
            renderFileInput(
              'procuration', 
              'Procuration', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('procuration')
            )
          )}

          {shouldShowField('Convention_OuvertureFile') && (
            renderFileInput(
              'Convention_OuvertureFile', 
              'Convention d\'ouverture du compte', 
              IMAGE_TYPES, 
              MAX_IMAGE_SIZE, 
              false
            )
          )}
          {shouldShowField('KycFile') && (
            renderFileInput(
              'KycFile', 
              'KYC', 
              IMAGE_TYPES, 
              MAX_IMAGE_SIZE, 
              false
            )
          )}
          {shouldShowField('Formulaire_OuvertureFile') && (
            renderFileInput(
              'Formulaire_OuvertureFile', 
              'Formulaire d\'ouverture du compte', 
              IMAGE_TYPES, 
              MAX_IMAGE_SIZE, 
              false
            )
          )}
        </div>
      </div>

      {/* Documents Optionnels */}
      <div className="card">
        <div className="card-header bg-light text-center">
          <h5 className="mb-0 text-black">Documents Optionnels</h5>
        </div>
        <div className="card-body">
          {shouldShowField('signatureImage') && (
            renderFileInput(
              'signatureImage', 
              'Signature', 
              IMAGE_TYPES, 
              MAX_IMAGE_SIZE, 
              false
            )
          )}

          {shouldShowField('rccm') && (
            renderFileInput(
              'rccm', 
              'RCCM', 
              DOCUMENT_TYPES, 
              MAX_DOCUMENT_SIZE, 
              isFieldRequired('rccm')
            )
          )}
        </div>
      </div>

      {/* Instructions spécifiques pour mode édition */}
      {isUpdateMode && (
        <div className="alert alert-info mt-4">
          <div className="d-flex align-items-start">
            <FaEye className="me-3 mt-1" size={20} />
            <div>
              <h6 className="alert-heading">Mode Édition - Gestion des documents</h6>
              <ul className="mb-0">
                <li>Les fichiers existants sont affichés avec des boutons <FaEye className="mx-1" size={12} /> (visualiser) et <FaTrash className="mx-1" size={12} /> (supprimer)</li>
                <li>Pour remplacer un fichier : téléchargez un nouveau fichier dans le champ correspondant</li>
                <li>Pour conserver le fichier actuel : laissez le champ vide</li>
                <li>Les documents marqués d'un <span className="badge bg-success bg-opacity-10 border-opacity-25 border-success text-success rounded-2">Fichier présent</span> ne sont plus obligatoires</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Instructions spécifiques pour comptes administrés */}
      {isManagedAccount && (
        <div className="alert alert-warning mt-4">
          <div className="d-flex align-items-start">
            <FaFile className="me-3 mt-1" size={20} />
            <div>
              <h6 className="alert-heading">Compte Administré - Restrictions</h6>
              <ul className="mb-0">
                <li><strong>CNI</strong> : Non requis pour les comptes administrés</li>
                <li><strong>Justificatif de domicile</strong> : Non requis pour les comptes administrés</li>
                <li><strong>Email</strong> : Non requis, toutes les communications passent par les représentants légaux</li>
                <li>Les représentants légaux ne peuvent pas être modifiés via ce formulaire</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;